import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { collectEvent, UserAction, userActionTypeEnum, } from '@thoughtspot/metrics';
import { ErrorCode } from '@thoughtspot/radiant-react/widgets/alert/alert-constants';
import { Button } from '@thoughtspot/radiant-react/widgets/button/button';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { SearchBar } from '@thoughtspot/radiant-react/widgets/search-bar';
import { getGuidsToSkip, getPrincipalObjectExportLimit, globalBannerObserverSubscriber, isOrgUiEnabled, isPrincipalImportExportEnabled, isScriptabilityEnabled, isTmlObjectIdEnabled, orgStatisticsCountV2Enabled, } from '@thoughtspot/session-service';
import { getClientId, isOktaEnabled } from '@thoughtspot/system-config-service';
import { isNil } from 'lodash';
import React, { useCallback, useEffect, useRef, useState, } from 'react';
import { ValidEdocMetadataType } from "../../../services/generated/graphql-types";
import { useAddUsersToOrgMutation, useAssignUsersToGroupsFromUserMutation, useDeleteUserMutation, useGenerateOtpMutation, useGetEdocLazyQuery, useGetOrgStatisticsMutation, useGetUsersListPrincipalQuery, useReInviteMemberMutation, useResetMfaConifgMutation, useUpdateUserMutation, } from "../../../services/user-group/user-group-service";
import { BasePageContext } from "../../../contexts/basepage-context";
import { getTranslatedInfoForMessageCode } from "../../../utils/error-handling/error-strings-mapping";
import { getErrorObjectFromGraphQLErrors } from "../../../utils/error-handling/error-utils";
import { BaseComponentContainer } from '../base-component/base-component.container';
import { ListContainer } from '../list-container/list.container';
import { captureFailureEvent, deepOmit, getErrorCode, getSelectedOnCurrentPage, isExportObjectLimitReached, MODE, onRowsSelectAction, refreshCells, updateRowData, } from '../util';
import { AddUsersToGroupsModalContainer } from './add-users-to-groups-modal/add-users-to-groups-modal.container';
import { AddUsersToOrgModalContainer } from './add-users-to-org-modal/add-users-to-org-modal.container';
import { CreateEditModal } from './create-edit-modal/create-edit-modal.container';
import { DeleteConfirmationModal } from './delete-confirmation-modal/delete-confirmation-modal';
import { ExportTMLOptions } from './ExportUserTMLoptions';
import { ResetPasswordConfirmationModal } from './reset-password-confirmation-modal/reset-password-confirmation-modal';
import { useDataHooks } from './useDataHook';
import styles from './users.module.scss';
import { ExportTML } from './usersExportModal';
import { downloadExportedUsers, getColumnDefinitions, getDefaultSamlHeaders, getToastMessage, isFreeTrialOrTeamOrDeveloperUser, ListActionType, sortKeyMap, tableColumnHeaders, TranslationsKeys, userParams, } from './util';
export var ImportEPackAction;
(function (ImportEPackAction) {
    ImportEPackAction["Create"] = "CREATE";
    ImportEPackAction["Update"] = "UPDATE";
})(ImportEPackAction || (ImportEPackAction = {}));
const initialModalOpen = {
    isModalOpened: false,
    isDeleteModalOpen: false,
    isAddUsersToGroupsModalOpen: false,
    isAddUsersToOrgModalOpen: false,
    isResetPasswordModalOpen: false,
};
export const UsersContainer = ({ currentOrgId, }) => {
    const { t } = useTranslation();
    const { metadataService, metadataList } = BasePageContext();
    const isTmlObjectIdFlagEnabled = isTmlObjectIdEnabled();
    const currentData = useRef();
    const gridRef = useRef(null);
    const [rowData, setRowData] = useState([]);
    const [errorBanner, setErrorBanner] = useState(false);
    const [errorBannerMessage, setErrorBannerMessage] = useState(null);
    const [isToastAlertVisible, setIsToastAlertVisible] = useState(false);
    const [mode, setMode] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isRefresh, setIsRefresh] = useState(false);
    const [modalOpened, setModalOpened] = useState(initialModalOpen);
    const [isEditMode, setIsEditMode] = useState(false);
    const [userListInput, setUserListInput] = useState({
        ...userParams,
        isAllOrgContext: currentOrgId === -1,
    });
    const [shouldExportDependencies, setShouldExportDependencies] = useState(true);
    const [isExportModalOpen, setIsExportModalOpened] = useState(false);
    const [selectedRowsIds, setSelectedRowsIds] = useState([]);
    const [lastOperatedPage, setLastOperatedPage] = useState(0);
    const [hasInitialOrgCount, setHasInitialOrgCount] = useState(false);
    const [generatedOtp, setGeneratedOtp] = useState('');
    const [resetPasswordLoader, setResetPasswordLoader] = useState(false);
    const [resetPasswordOtpLoader, setResetPasswordOtpLoader] = useState(false);
    const [otpErrorMessage, setOtpErrorMessage] = useState('');
    const { generateOtp, data: otpData, loading: otpLoading, error: otpError, } = useGenerateOtpMutation();
    const showErrorBanner = (message) => {
        setErrorBanner(true);
        setErrorBannerMessage(message);
    };
    const { resetMfaConfig, data: resetMfaResponse, loading: resetMfaLoading, error: resetMfaError, } = useResetMfaConifgMutation();
    const { getOrgStatistics, count: orgCount, loading: orgCountLoading, error: orgCountError, } = useGetOrgStatisticsMutation();
    const { getEdoc: getEdocLazyQuery, data: edocData, loading: isGetEdocLoading, error: isEdocError, } = useGetEdocLazyQuery();
    useEffect(() => {
        if (!hasInitialOrgCount && orgStatisticsCountV2Enabled()) {
            getOrgStatistics('USER', {
                allOrgs: currentOrgId === -1,
            });
            setHasInitialOrgCount(true);
        }
    }, [hasInitialOrgCount, currentOrgId]);
    useEffect(() => {
        if (isEdocError && !isGetEdocLoading && !edocData) {
            showErrorBanner(t(TranslationsKeys.EXPORT_LIMIT_ERROR, {
                limit: getPrincipalObjectExportLimit(),
            }));
            setIsLoading(false);
            setIsExportModalOpened(false);
        }
        else if (edocData && !isGetEdocLoading) {
            downloadExportedUsers(edocData, shouldExportDependencies);
            setIsLoading(false);
            setIsExportModalOpened(false);
            setSelectedRowsIds([]);
        }
    }, [edocData, isGetEdocLoading, isEdocError]);
    useEffect(() => {
        if (!resetMfaLoading) {
            if (resetMfaError) {
                setIsLoading(false);
                setErrorBannerMessage(t(TranslationsKeys.RESET_MFA_ERROR_MSG));
                setErrorBanner(true);
            }
            else if (resetMfaResponse) {
                setMode(MODE.RESET_MFA);
                setIsToastAlertVisible(true);
                setIsLoading(false);
            }
        }
    }, [resetMfaError, resetMfaResponse, resetMfaLoading]);
    const { data: updateUserData, error: updateUserError, loading: updateUserLoading, updateUser, } = useUpdateUserMutation();
    const sortData = useRef({
        sort: userParams.sort,
        sortAscending: userParams.sortAscending,
    });
    const { data, error, loading, isLastBatch } = useDataHooks(useGetUsersListPrincipalQuery, isRefresh, userListInput, setIsLoading, setIsRefresh);
    const { data: deleteUserData, error: deleteUserError, loading: deleteUserLoading, deleteUser, } = useDeleteUserMutation();
    const { data: assignUsersToGroupsData, error: assignUsersToGroupsError, loading: assignUsersToGroupsLoading, assignUsersToGroupsFromUser, } = useAssignUsersToGroupsFromUserMutation();
    const { data: createOrgData, error: createOrgError, loading: createOrgLoading, addUsersToOrg, } = useAddUsersToOrgMutation();
    const hideErrorBanner = () => {
        setErrorBanner(false);
        setErrorBannerMessage(null);
    };
    const refreshOrgCount = useCallback(() => {
        if (!orgStatisticsCountV2Enabled()) {
            return;
        }
        getOrgStatistics('USER', {
            allOrgs: currentOrgId === -1,
        });
    }, [getOrgStatistics, currentOrgId]);
    const successBannerMode = (successMode) => {
        setMode(successMode);
        setIsToastAlertVisible(true);
        setIsRefresh(true);
        hideErrorBanner();
        if (successMode === MODE.DELETE ||
            successMode === MODE.ADD ||
            successMode === MODE.ASSIGN_USERS_ORG) {
            refreshOrgCount();
        }
    };
    const onChangeOffset = (offset) => {
        const { sort, sortAscending } = sortData.current;
        setUserListInput({
            ...userListInput,
            offset,
            sort,
            sortAscending,
            skipIds: getGuidsToSkip(),
        });
        setIsLoading(true);
    };
    const noDataProps = {
        title: t('NO_MATCH_MSG'),
        description: '',
        img: ErrorCode.GLOBAL_DATA,
    };
    const onSearchChange = (term) => {
        const { sort, sortAscending } = sortData.current;
        setUserListInput({
            ...userListInput,
            offset: 0,
            sort,
            sortAscending,
            pattern: term,
            skipIds: getGuidsToSkip(),
        });
        setIsLoading(true);
    };
    const onClickedImportButton = () => {
        localStorage.setItem('allOrgsOverride', currentOrgId === -1 ? 'true' : 'false');
        window.location.href = `${window.location.origin}/#/import-tsl/USER`;
    };
    const openEditModal = useCallback((rowData) => {
        currentData.current = rowData;
        setModalOpened({
            ...initialModalOpen,
            isModalOpened: true,
        });
        setIsEditMode(true);
        setIsLoading(true);
        const updateUserClickAction = new UserAction(userActionTypeEnum.UPDATE_USER_CLICKED);
        collectEvent(updateUserClickAction, {
            isIamV2: isOktaEnabled(),
            timestamp: Date.now(),
        });
    }, []);
    const suppressNavigation = ({ colDef, }) => colDef.colId === tableColumnHeaders.MORE_OPTION;
    const onClickAddUserBtn = () => {
        hideErrorBanner();
        setModalOpened({
            ...initialModalOpen,
            isModalOpened: true,
        });
        setIsEditMode(false);
        setIsLoading(true);
        const createUserClickAction = new UserAction(userActionTypeEnum.ADD_USER_CLICKED);
        collectEvent(createUserClickAction, {
            isIamV2: isOktaEnabled(),
            timestamp: Date.now(),
        });
    };
    const onClickDeleteUserBtn = useCallback(() => {
        hideErrorBanner();
        setModalOpened({
            ...initialModalOpen,
            isDeleteModalOpen: true,
        });
    }, [modalOpened.isDeleteModalOpen]);
    const onClickAddUsersToGroupsBtn = useCallback(() => {
        hideErrorBanner();
        setModalOpened({
            ...initialModalOpen,
            isAddUsersToGroupsModalOpen: true,
        });
    }, [modalOpened.isAddUsersToGroupsModalOpen]);
    const onClickAddUsersToOrgBtn = useCallback(() => {
        hideErrorBanner();
        setModalOpened({
            ...initialModalOpen,
            isAddUsersToOrgModalOpen: true,
        });
    }, [modalOpened.isAddUsersToOrgModalOpen]);
    const renderActionsBtn = (text, onClick, isPrimaryBtn = false) => (React.createElement(Button, { text: t(TranslationsKeys[text]), onClick: onClick, type: isPrimaryBtn ? 'primary' : 'secondary' }));
    const isTmlImportExportEnabled = isPrincipalImportExportEnabled() && isScriptabilityEnabled();
    const getActionBar = useCallback(() => (React.createElement(Horizontal, { className: styles.actionHandlerWrapper },
        React.createElement(React.Fragment, null,
            React.createElement(Vertical, { className: styles.gridSearch },
                React.createElement(SearchBar, { searchValue: "", searchPlaceholder: t('cirrus.metadata.search.placeHolder'), onSearchChange: (searchTerm) => {
                        onSearchChange(searchTerm);
                    }, className: styles.searchBarContainer, "data-testid": "search-bar-filters" })),
            !selectedRowsIds.length ? (React.createElement(Horizontal, { spacing: "d" },
                renderActionsBtn('ADD_USER_BUTTON_TITLE', onClickAddUserBtn),
                isTmlImportExportEnabled &&
                    renderActionsBtn('IMPORT_USER_TITLE', onClickedImportButton))) : (React.createElement(Horizontal, { spacing: "d" },
                isOrgUiEnabled() &&
                    currentOrgId === -1 &&
                    renderActionsBtn('ADD_USERS_TO_ORG', onClickAddUsersToOrgBtn),
                (!isOrgUiEnabled() || currentOrgId !== -1) &&
                    renderActionsBtn('ADD_USERS_TO_GROUPS', onClickAddUsersToGroupsBtn),
                isTmlImportExportEnabled &&
                    renderActionsBtn('EXPORT', () => {
                        if (isExportObjectLimitReached(selectedRowsIds)) {
                            showErrorBanner(t(TranslationsKeys.EXPORT_LIMIT_ERROR, {
                                limit: getPrincipalObjectExportLimit(),
                            }));
                        }
                        else {
                            setIsExportModalOpened(true);
                        }
                    }),
                renderActionsBtn('DELETE', onClickDeleteUserBtn)))))), [selectedRowsIds, rowData]);
    const onRowsSelected = (rowIds) => {
        onRowsSelectAction(rowIds, data, userListInput, lastOperatedPage, setLastOperatedPage, selectedRowsIds, setSelectedRowsIds);
    };
    useEffect(() => {
        const filterredSelectedRowIds = getSelectedOnCurrentPage(selectedRowsIds, data);
        if (gridRef.current) {
            updateRowData(rowData, filterredSelectedRowIds, setRowData);
            refreshCells(gridRef, filterredSelectedRowIds, tableColumnHeaders.MORE_OPTION);
        }
    }, [selectedRowsIds]);
    useEffect(() => {
        updateRowData(data, getSelectedOnCurrentPage(selectedRowsIds, data), setRowData);
        setIsLoading(false);
    }, [data]);
    const updateUserState = async (userId, state) => {
        const newFormData = {
            userid: userId,
            content: {},
            password: '',
            userAccountState: state,
            isAllOrgContext: currentOrgId === -1,
            triggeredbyadmin: true,
        };
        await updateUser(newFormData)
            .then(() => {
            setIsRefresh(true);
            const usersAction = new UserAction(userActionTypeEnum.RESEND_ACTIVATION_EMAIL);
            const { password, ...rest } = newFormData;
            collectEvent(usersAction, {
                ...rest,
                timestamp: Date.now(),
                isIamV2: isOktaEnabled(),
            });
            if (state === 'reactivate') {
                setMode(MODE.RESEND_ACTIVATION);
                setIsToastAlertVisible(true);
            }
            return undefined;
        })
            .catch((error) => {
            if (state === 'reactivate') {
                setErrorBannerMessage(t(TranslationsKeys.RE_INVITE_ERROR_MSG));
                setErrorBanner(true);
            }
        })
            .finally(() => {
            setIsLoading(false);
        });
    };
    const exportUsers = (userIds) => {
        setIsLoading(true);
        const payload = userIds.map(id => ({
            id,
            type: ValidEdocMetadataType.User,
        }));
        getEdocLazyQuery(payload, shouldExportDependencies, false, currentOrgId === -1, isTmlObjectIdFlagEnabled, isTmlObjectIdFlagEnabled);
    };
    const { data: reInviteData, error: reInviteError, loading: reInviteLoading, reInviteMember, } = useReInviteMemberMutation();
    useEffect(() => {
        if (!reInviteLoading) {
            if (!isNil(reInviteData)) {
                setMode(MODE.RESEND_ACTIVATION);
                setIsToastAlertVisible(true);
                setIsLoading(false);
                setIsRefresh(true);
            }
            if (reInviteError) {
                setErrorBannerMessage(t(TranslationsKeys.RE_INVITE_ERROR_MSG));
                setErrorBanner(true);
                setIsLoading(false);
            }
        }
    }, [reInviteData, reInviteError, reInviteLoading]);
    useEffect(() => {
        let isSubscribed = true;
        const handleGlobalBannerNotification = () => {
            if (!isSubscribed)
                return;
            const currentUrl = window.location.href;
            const isAdminPage = currentUrl.includes('/admin');
            const activeTabId = localStorage.getItem('AdminLandingTabId');
            const isUsersTabActive = activeTabId === 'users' || (!activeTabId && isAdminPage);
            if (isAdminPage && isUsersTabActive) {
                setIsRefresh(true);
            }
        };
        globalBannerObserverSubscriber(handleGlobalBannerNotification);
        return () => {
            isSubscribed = false;
        };
    }, []);
    const onRowActionCB = ({ action, data: actionData }) => {
        currentData.current = actionData;
        if (action === ListActionType.EDIT) {
            openEditModal(actionData);
        }
        else if (action === ListActionType.DELETE) {
            setModalOpened({
                ...initialModalOpen,
                isDeleteModalOpen: true,
            });
        }
        else if (action === ListActionType.RESEND_ACTIVATION_MAIL) {
            if (isFreeTrialOrTeamOrDeveloperUser()) {
                setIsLoading(true);
                reInviteMember(actionData.id);
            }
            else {
                updateUserState(actionData.id, 'reactivate');
                setIsLoading(true);
            }
        }
        else if (action === ListActionType.EXPORT) {
            exportUsers([actionData.id]);
        }
        else if (action === ListActionType.UNLOCK) {
            updateUserState(actionData.id, 'unlock');
            setIsLoading(true);
        }
        else if (action === ListActionType.UNSUSPEND) {
            updateUserState(actionData.id, 'unsuspend');
            setIsLoading(true);
        }
        else if (action === ListActionType.RESET_MFA) {
            setIsLoading(true);
            resetMfaConfig({
                clientId: getClientId(),
                defaultHeaders: getDefaultSamlHeaders(),
                userId: actionData.userName.text,
            });
        }
        else if (action === ListActionType.RESET_PASSWORD) {
            setModalOpened({
                ...initialModalOpen,
                isResetPasswordModalOpen: true,
            });
        }
    };
    const onDeleteModalClose = useCallback(() => {
        setModalOpened({
            ...initialModalOpen,
            isDeleteModalOpen: false,
        });
    }, [modalOpened.isDeleteModalOpen]);
    const onAddUsersToGroupsModalClose = useCallback(() => {
        setModalOpened({
            ...initialModalOpen,
            isAddUsersToGroupsModalOpen: false,
        });
    }, [modalOpened.isAddUsersToGroupsModalOpen]);
    const onAddUsersToOrgModalClose = useCallback(() => {
        setModalOpened({
            ...initialModalOpen,
            isAddUsersToOrgModalOpen: false,
        });
    }, [modalOpened.isAddUsersToOrgModalOpen]);
    const onResetPasswordModalClose = useCallback(() => {
        setModalOpened({
            ...initialModalOpen,
            isResetPasswordModalOpen: false,
        });
        setGeneratedOtp('');
        setOtpErrorMessage('');
        setResetPasswordOtpLoader(false);
        setResetPasswordLoader(false);
    }, []);
    const onDeleteConfirm = async () => {
        hideErrorBanner();
        let ids = [currentData.current?.id];
        if (selectedRowsIds.length > 0) {
            ids = [...selectedRowsIds];
        }
        await deleteUser(ids, currentOrgId === -1)
            .then(res => {
            onDeleteModalClose();
            successBannerMode(MODE.DELETE);
            setSelectedRowsIds([]);
            const deleteUsersAction = new UserAction(userActionTypeEnum.CREATE_USER);
            const properties = {
                users: ids,
                timestamp: Date.now(),
                isIamV2: isOktaEnabled(),
            };
            collectEvent(deleteUsersAction, properties);
            const { offset, batchSize } = userListInput;
            if (offset && ids.length === rowData.length) {
                onChangeOffset(offset - batchSize);
            }
            return '';
        })
            .catch(err => {
            const graphQlError = getErrorObjectFromGraphQLErrors(err);
            const errorCode = getErrorCode(graphQlError);
            const translatedErrorInfo = getTranslatedInfoForMessageCode(errorCode);
            const errorText = t(translatedErrorInfo.summary);
            setErrorBannerMessage(errorText);
            setErrorBanner(true);
            setSelectedRowsIds([]);
            onDeleteModalClose();
            const userAction = new UserAction(userActionTypeEnum.CREATE_USER);
            captureFailureEvent(userAction.name, graphQlError, errorText);
        });
    };
    const onAddUsersToGroupsConfirm = async (payload) => {
        hideErrorBanner();
        const finalPayload = deepOmit(payload, [
            '__typename',
            'displayNameLastUpdatedBy',
        ]);
        if (finalPayload) {
            for (let i = 0; i < finalPayload.length; i++) {
                const locale = finalPayload[i].userContent.userPreferences.preferredLocale;
                if (!locale || locale === null) {
                    delete finalPayload[i].userContent.userPreferences
                        .preferredLocale;
                }
                const persona = finalPayload[i].userContent.userProperties.persona;
                if (!persona || persona === null) {
                    delete finalPayload[i].userContent.userProperties.persona;
                }
            }
        }
        assignUsersToGroupsFromUser({
            content: finalPayload,
        })
            .then(() => {
            successBannerMode(MODE.ASSIGN_USERS_GROUPS);
            setSelectedRowsIds([]);
            onAddUsersToGroupsModalClose();
            const userAction = new UserAction(userActionTypeEnum.ASSIGN_USERS_TO_GROUPS);
            collectEvent(userAction, {
                isIamV2: isOktaEnabled(),
                timestamp: Date.now(),
            });
            return null;
        })
            .catch(err => {
            const graphQlError = getErrorObjectFromGraphQLErrors(err);
            const errorCode = getErrorCode(graphQlError);
            const translatedErrorInfo = getTranslatedInfoForMessageCode(errorCode);
            const errorText = t(translatedErrorInfo.summary);
            setErrorBannerMessage(errorText);
            setErrorBanner(true);
            setSelectedRowsIds([]);
            onAddUsersToGroupsModalClose();
            const userAction = new UserAction(userActionTypeEnum.ASSIGN_USERS_TO_GROUPS);
            captureFailureEvent(userAction.name, graphQlError, errorText);
        });
    };
    const onAddUsersToOrgConfirm = async (payload) => {
        hideErrorBanner();
        const input = {
            userids: selectedRowsIds,
            orgid: Number(payload),
        };
        addUsersToOrg(input)
            .then(() => {
            successBannerMode(MODE.ASSIGN_USERS_ORG);
            setSelectedRowsIds([]);
            onAddUsersToOrgModalClose();
            const userAction = new UserAction(userActionTypeEnum.ASSIGN_USERS_TO_ORG);
            const properties = {
                users: input.userids,
                orgid: input.orgid,
                timestamp: Date.now(),
                isIamV2: isOktaEnabled(),
            };
            collectEvent(userAction, properties);
            return null;
        })
            .catch(err => {
            const graphQlError = getErrorObjectFromGraphQLErrors(err);
            const errorCode = getErrorCode(graphQlError);
            const translatedErrorInfo = getTranslatedInfoForMessageCode(errorCode);
            const errorText = t(translatedErrorInfo.summary);
            setErrorBannerMessage(errorText);
            setErrorBanner(true);
            setSelectedRowsIds([]);
            onAddUsersToGroupsModalClose();
            const userAction = new UserAction(userActionTypeEnum.ASSIGN_USERS_TO_ORG);
            captureFailureEvent(userAction.name, graphQlError, errorText);
        });
    };
    const onGenerateOtp = async () => {
        try {
            setResetPasswordOtpLoader(true);
            setOtpErrorMessage('');
            const response = await generateOtp(currentData.current?.id);
            if (response.data && response.data.Session__generateOtp) {
                setGeneratedOtp(response.data.Session__generateOtp);
            }
            else {
                setGeneratedOtp('');
                setOtpErrorMessage(TranslationsKeys.OTP_GENERATION_FAILED);
            }
            setResetPasswordOtpLoader(false);
        }
        catch (error) {
            setResetPasswordOtpLoader(false);
            setOtpErrorMessage(TranslationsKeys.OTP_GENERATION_FAILED);
        }
    };
    const onResetPasswordConfirm = async () => {
        onResetPasswordModalClose();
        successBannerMode(MODE.RESET_PASSWORD);
    };
    const errorConfig = {
        errorBanner,
        errorBannerMessage,
        hideErrorBanner,
    };
    const onSortingChanged = (sortState) => {
        const sortingColumn = sortState.find((el) => el.sort !== 'null') || sortState[0];
        const sortKey = sortingColumn &&
            sortKeyMap[sortingColumn.colId];
        const ascending = sortingColumn ? sortingColumn.sort === 'asc' : true;
        sortData.current.sort = sortKey;
        sortData.current.sortAscending = ascending;
        setUserListInput({
            ...userListInput,
            sort: sortKey,
            offset: 0,
            skipIds: getGuidsToSkip(),
            sortAscending: ascending,
        });
        setIsLoading(true);
    };
    const setAgGridApi = (agGridApi) => {
        gridRef.current = agGridApi;
    };
    const listViewProps = {
        data: rowData,
        columns: getColumnDefinitions(),
        options: {
            suppressMovable: true,
            hasCheckboxes: true,
            onRowsSelected,
            selectedRowsIds,
            checkboxHeaderClass: [styles.headerStyle],
            customWidth: 48,
        },
        agGridContext: {
            onRowAction: onRowActionCB,
        },
        onSortingChanged,
        loading,
        setAgGridApi,
        suppressKeyboardEvent: (params) => params.colDef.colId === tableColumnHeaders.MORE_OPTION,
    };
    const onExportModalRadioButtonChange = (event) => {
        setShouldExportDependencies(event.target.value === ExportTMLOptions.OBJECT_WITH_DEPENDENCIES);
    };
    const shouldShowOrgCount = orgStatisticsCountV2Enabled() &&
        !orgCountLoading &&
        currentOrgId !== -1;
    return (React.createElement(BaseComponentContainer, { id: "users-page", container: "usersContainer", title: shouldShowOrgCount
            ? `${t(TranslationsKeys.PAGE_TITLE)} (${orgCount ?? 0})`
            : `${t(TranslationsKeys.PAGE_TITLE)}`, error: errorConfig },
        React.createElement(Vertical, null,
            React.createElement(LoadingIndicator.Global, { hideLoader: !isLoading },
                React.createElement(Vertical, null,
                    getActionBar(),
                    React.createElement(ListContainer, { testId: "users-list-page", data: data, listViewProps: listViewProps, noDataProps: noDataProps, offSet: userListInput.offset, paginationSize: userListInput.batchSize, isLastBatch: isLastBatch, onChangeOffset: onChangeOffset, headerHeight: 32, isLoading: isLoading, onKeyDown: e => {
                            const focusedCell = gridRef.current?.getFocusedCell();
                            if (e.key === 'Enter' &&
                                gridRef.current
                                    ?.getFocusedCell()
                                    ?.column?.getColId() ===
                                    tableColumnHeaders.USER_NAME) {
                                const rowNode = gridRef.current.getDisplayedRowAtIndex(focusedCell.rowIndex);
                                if (rowNode) {
                                    openEditModal(rowNode.data);
                                }
                            }
                        } }, metadataList)),
                modalOpened.isModalOpened && (React.createElement(CreateEditModal, { isModalOpened: modalOpened.isModalOpened, closeModal: () => {
                        setModalOpened({
                            ...initialModalOpen,
                            isModalOpened: !modalOpened.isModalOpened,
                        });
                        setIsLoading(false);
                    }, isEditMode: isEditMode, setIsLoading: setIsLoading, successBannerMode: successBannerMode, userId: currentData.current?.id, currentOrgId: currentOrgId })),
                isExportModalOpen && (React.createElement(ExportTML, { onRadioButtonChange: onExportModalRadioButtonChange, onModalDismiss: () => {
                        setIsExportModalOpened(false);
                    }, getEdoc: () => exportUsers(selectedRowsIds), isGetEdocLoading: isGetEdocLoading, shouldExportDependencies: shouldExportDependencies, isExportDependenciesModalOpen: isExportModalOpen })),
                modalOpened.isDeleteModalOpen && (React.createElement(DeleteConfirmationModal, { name: currentData.current?.userDisplayName.text || '', isModalOpen: modalOpened.isDeleteModalOpen, onDeleteConfirm: onDeleteConfirm, onDeleteModalClose: onDeleteModalClose, isMultiSelected: selectedRowsIds.length > 0, showLoader: deleteUserLoading })),
                modalOpened.isAddUsersToGroupsModalOpen && (React.createElement(AddUsersToGroupsModalContainer, { isModalOpen: modalOpened.isAddUsersToGroupsModalOpen, onAddUsersToGroupsConfirm: onAddUsersToGroupsConfirm, onAddUsersToGroupsModalClose: onAddUsersToGroupsModalClose, userIds: [...selectedRowsIds] })),
                modalOpened.isAddUsersToOrgModalOpen && (React.createElement(AddUsersToOrgModalContainer, { isModalOpen: modalOpened.isAddUsersToOrgModalOpen, onAddUsersToOrgConfirm: onAddUsersToOrgConfirm, onAddUsersToOrgModalClose: onAddUsersToOrgModalClose })),
                modalOpened.isResetPasswordModalOpen && (React.createElement(ResetPasswordConfirmationModal, { isModalOpen: modalOpened.isResetPasswordModalOpen, onModalClose: onResetPasswordModalClose, onResetConfirm: onResetPasswordConfirm, onGenerateOtp: onGenerateOtp, generatedOtp: generatedOtp, showLoader: resetPasswordLoader, showOtpLoader: resetPasswordOtpLoader, otpErrorMessage: otpErrorMessage }))),
            React.createElement(GlobalAlert.Toast, { message: getToastMessage(mode, listViewProps.options.selectedRowsIds.length), isVisible: isToastAlertVisible, onClose: () => setIsToastAlertVisible(false), autoHide: true }))));
};
export const Users = ({ currentOrgId }) => (React.createElement(UsersContainer, { currentOrgId: currentOrgId }));
//# sourceMappingURL=users.container.js.map